import React from "react";
import { handleDynamicImportLoadError } from "../monitoring/chunkLoadRecovery.js";
import { captureError } from "../monitoring/sentry.js";
import { logger } from "../utils/logger";

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidUpdate(prevProps) {
    if (!this.state.hasError) return;
    if (!resetKeysChanged(prevProps.resetKeys, this.props.resetKeys)) return;
    this.resetErrorBoundary();
  }

  componentDidCatch(error, info) {
    const boundaryName = this.props.name || "root";
    logger.error(`[ErrorBoundary:${boundaryName}] Uncaught render error:`, error, info);
    captureError(error, { boundaryName, reactErrorInfo: info });
    handleDynamicImportLoadError(error);
    this.props.onError?.(error, info);
  }

  resetErrorBoundary = () => {
    this.setState({ hasError: false, error: null });
    this.props.onReset?.();
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback({
          error: this.state.error,
          resetErrorBoundary: this.resetErrorBoundary,
        });
      }

      return (
        <div
          style={{
            position: "fixed",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: "rgba(0, 0, 0, 0.85)",
          }}
        >
          <div
            className="menu-panel"
            style={{
              padding: "32px 36px 36px",
              maxWidth: 480,
              width: "calc(100vw - 48px)",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 16,
              textAlign: "center",
            }}
          >
            <div
              style={{
                fontSize: 18,
                fontWeight: 600,
                color: "var(--panel-text-primary)",
                letterSpacing: "-0.01em",
              }}
            >
              Something went wrong
            </div>
            <div
              className="menu-text-muted"
              style={{ fontSize: 13, lineHeight: 1.6, maxWidth: 380 }}
            >
              An unexpected error occurred. Try refreshing the page. If the problem persists, check
              the browser console for details.
            </div>
            <button
              className="menu-button"
              onClick={() => window.location.reload()}
              style={{ marginTop: 4 }}
            >
              Reload
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

function resetKeysChanged(prevResetKeys = [], resetKeys = []) {
  if (prevResetKeys.length !== resetKeys.length) return true;
  return resetKeys.some((key, index) => !Object.is(key, prevResetKeys[index]));
}

export default ErrorBoundary;
